import React, { useCallback, useEffect, useState } from 'react'
import { BusyContent, LoadingIndicator, PageHeader, StatusBadge } from '../components/ui/Primitives';
import axios from 'axios'
import UserActionTable from '../components/UserActionTable';
import './AdminMetrics.css'
import jwt from 'jwt-decode';
import ObservabilityCenter from '../components/ObservabilityCenter';
import { logSectionView } from '../utils/logger';

const OBSERVABILITY_TABS = [
  { id: 'health', label: 'Health', description: 'Services and performance' },
  { id: 'calls', label: 'Call performance', description: 'Audio timing and trends' },
  { id: 'agents', label: 'Agents & tools', description: 'Runs, tools, and traces' },
  { id: 'logging', label: 'Logs', description: 'Delivery and rotation' },
  { id: 'crm', label: 'CRM operations', description: 'Sync and governance' },
  { id: 'events', label: 'Latest events', description: 'Complete activity stream' }
]

const OVERVIEW_TAB_IDS = ['health', 'calls', 'agents', 'logging']

const readTabFromLocation = () => {
  if (typeof window === 'undefined') return OBSERVABILITY_TABS[0].id
  const requestedTab = window.location.hash.replace(/^#/, '')
  return OBSERVABILITY_TABS.some(tab => tab.id === requestedTab)
    ? requestedTab
    : OBSERVABILITY_TABS[0].id
}


// Query to get user by email
// select * from "user" where email='dale+ultra@chase-ai.com'


const formatDate = (value) => {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString()
}

const titleCase = (value) => String(value || 'unknown').replace(/_/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase())

const pipedriveContactErrors = {
  PIPEDRIVE_CONTACT_MATCH_AMBIGUOUS: 'Multiple contacts match. Review the contact before saving the linked record.',
  PIPEDRIVE_CONTACT_DETAILS_CONFLICT: 'Contact email or phone conflicts with existing details. Review the contact.',
  PIPEDRIVE_CONTACT_CREATE_UNCERTAIN: 'Check Pipedrive for the contact before attempting creation again.',
  PIPEDRIVE_CONTACT_PHONE_INVALID: 'The contact phone number needs a country code.',
  PIPEDRIVE_CONTACT_EMAIL_INVALID: 'The contact email address needs correction.',
};

function CrmOperations() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [retryingId, setRetryingId] = useState('')
  const [error, setError] = useState('')
  const backendHost = process.env.REACT_APP_BACKEND_HOST

  const authConfig = useCallback(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('jwt_authorization')}` }
  }), [])

  const loadOperations = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const response = await axios.get(`${backendHost}/v2/integrations/operations`, authConfig())
      setData(response.data)
    } catch (requestError) {
      setError(requestError.response?.data?.error?.message || 'CRM operations data could not be loaded.')
    } finally {
      setLoading(false)
    }
  }, [authConfig, backendHost])

  useEffect(() => {
    loadOperations()
  }, [loadOperations])

  const retryJob = async (jobId) => {
    setRetryingId(jobId)
    setError('')
    try {
      await axios.post(`${backendHost}/v2/integrations/sync-jobs/${jobId}/retry`, {}, authConfig())
      await loadOperations()
    } catch (requestError) {
      setError(requestError.response?.data?.error?.message || 'The CRM sync could not be retried.')
    } finally {
      setRetryingId('')
    }
  }

  const health = data?.syncHealth || {}
  const statusCounts = health.byStatus || {}
  const jobs = data?.jobs || []
  const actionJobs = jobs.filter(job => ['failed', 'needs_review', 'processing'].includes(job.status))
  const connections = data?.connections || []
  const workflows = data?.workflowGovernance?.workflows || []
  const auditEvents = data?.auditEvents || []
  const systemHealth = data?.systemHealth || {}

  return (
    <section className="crm-operations-panel">
      <div className="crm-operations-heading">
        <div>
          <span className="admin-section-kicker">CRM operations</span>
          <h2>Integration health and governance</h2>
          <p>Monitor record delivery, connected accounts, published workflows, and operator actions.</p>
        </div>
        <button type="button" className="admin-refresh-button" onClick={loadOperations} disabled={loading}>
          <BusyContent busy={loading} label={"Refreshing…"}>{'Refresh'}</BusyContent>
        </button>
      </div>

      {error ? <div className="admin-operations-alert" role="alert">{error}</div> : null}

      <div className="crm-health-grid" aria-busy={loading}>
        <article><span>Delivery success</span><strong>{health.successRate === null || health.successRate === undefined ? '—' : `${health.successRate}%`}</strong><small>{statusCounts.succeeded || 0} successful writes</small></article>
        <article className={health.retryable ? 'attention' : ''}><span>Needs action</span><strong>{(health.retryable || 0) + (statusCounts.needs_review || 0)}</strong><small>{health.retryable || 0} can be retried</small></article>
        <article className={health.stalled ? 'danger' : ''}><span>Stalled jobs</span><strong>{health.stalled || 0}</strong><small>Processing longer than 15 minutes</small></article>
        <article><span>Active workflows</span><strong>{data?.workflowGovernance?.byStatus?.active || 0}</strong><small>{data?.workflowGovernance?.unpublishedChanges || 0} with unpublished changes</small></article>
      </div>

      <div className="crm-operations-grid">
        <article className="admin-operations-card admin-operations-wide system-health-card">
          <div className="admin-card-heading"><div><h3>System health</h3><p>Backend readiness and provider configuration required for CRM delivery.</p></div><span className={`operations-status ${systemHealth.backend === 'ready' ? 'connected' : 'failed'}`}>{!systemHealth.backend && loading ? <LoadingIndicator label="Checking system health…" /> : titleCase(systemHealth.backend || 'unknown')}</span></div>
          <div className="system-health-items">
            <div><span>Internal sync authentication</span><strong>{titleCase(systemHealth.internalSyncAuth)}</strong></div>
            {Object.entries(systemHealth.providerConfiguration || {}).map(([provider, status]) => <div key={provider}><span>{titleCase(provider)}</span><strong className={status === 'available' ? 'healthy' : 'unhealthy'}>{titleCase(status)}</strong></div>)}
          </div>
        </article>

        <article className="admin-operations-card admin-operations-wide">
          <div className="admin-card-heading"><div><h3>Delivery exceptions</h3><p>Recent CRM jobs requiring review or attention.</p></div><span>{actionJobs.length}</span></div>
          {actionJobs.length ? (
            <div className="admin-operations-table-wrap">
              <table className="admin-operations-table">
                <thead><tr><th>Status</th><th>Provider</th><th>Object</th><th>Call</th><th>Attempts</th><th>Last update</th><th>Action</th></tr></thead>
                <tbody>{actionJobs.map(job => (
                  <tr key={job.id}>
                    <td><span className={`operations-status ${job.status}`}>{titleCase(job.status)}</span>{job.provider === 'pipedrive' && job.result?.relatedRecords?.contact ? <small className="operations-muted">{job.result.relatedRecords.contact.status === 'saved' ? 'Contact saved; linked record pending' : job.status === 'processing' ? 'Saving contact...' : 'Contact creation outcome needs review'}</small> : null}{job.provider === 'pipedrive' && pipedriveContactErrors[job.error] ? <small className="operations-muted">{pipedriveContactErrors[job.error]}</small> : null}</td>
                    <td>{titleCase(job.provider)}</td>
                    <td>{job.targetObject}</td>
                    <td className="operations-mono" title={job.callSessionId}>{job.callSessionId || '—'}</td>
                    <td>{job.attemptCount}/{health.maxAttempts || 3}</td>
                    <td>{formatDate(job.dateUpdated || job.completedAt || job.startedAt)}</td>
                    <td>{job.status === 'failed' && job.attemptCount < (health.maxAttempts || 3) ? <button type="button" className="operations-retry-button" onClick={() => retryJob(job.id)} disabled={Boolean(retryingId)}><BusyContent busy={retryingId === job.id} label={"Retrying…"}>{'Retry'}</BusyContent></button> : <span className="operations-muted">Review</span>}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          ) : <div className="admin-operations-empty">No recent delivery exceptions.</div>}
        </article>

        <article className="admin-operations-card">
          <div className="admin-card-heading"><div><h3>Connected accounts</h3><p>Current provider authorization state.</p></div><span>{connections.length}</span></div>
          <div className="operations-list">{connections.length ? connections.map(connection => (
            <div className="operations-list-row" key={connection.id}>
              <div><strong>{titleCase(connection.provider)}</strong><small>{connection.crm_org_id || 'Account identifier unavailable'}</small></div>
              <span className={`operations-status ${connection.status}`}>{titleCase(connection.status)}</span>
            </div>
          )) : <div className="admin-operations-empty">No CRM accounts configured.</div>}</div>
        </article>

        <article className="admin-operations-card">
          <div className="admin-card-heading"><div><h3>Workflow governance</h3><p>Publishing state and pending configuration.</p></div><span>{workflows.length}</span></div>
          <div className="operations-list">{workflows.length ? workflows.map(workflow => (
            <div className="operations-list-row" key={workflow.id}>
              <div><strong>{workflow.name}</strong><small>Version {workflow.version}{workflow.hasUnpublishedChanges ? ' · unpublished changes' : ''}</small></div>
              <span className={`operations-status ${workflow.status}`}>{titleCase(workflow.status)}</span>
            </div>
          )) : <div className="admin-operations-empty">No receptionist workflows configured.</div>}</div>
        </article>

        <article className="admin-operations-card admin-operations-wide">
          <div className="admin-card-heading"><div><h3>Recent CRM audit activity</h3><p>Redacted operational history for connection, mapping, and delivery changes.</p></div><span>{auditEvents.length}</span></div>
          <div className="operations-audit-list">{auditEvents.length ? auditEvents.slice(0, 12).map(event => (
            <div className="operations-audit-row" key={event.id}>
              <span className={`operations-status ${event.result}`}>{titleCase(event.result)}</span>
              <div><strong>{titleCase(event.event_type)}</strong><small>{titleCase(event.provider)} · {event.actor_user_id ? 'User action' : 'System action'}</small></div>
              <time>{formatDate(event.date_created)}</time>
            </div>
          )) : <div className="admin-operations-empty">No CRM audit activity recorded.</div>}</div>
        </article>
      </div>
    </section>
  )
}

function AdminMetrics() {
  const [activeTab, setActiveTab] = useState(readTabFromLocation)

  useEffect(() => {
    // only logged in admins can access this page
    const jwt_token = localStorage.getItem('jwt_authorization');
    if (!jwt_token) {
      window.location.href = '/';
      return
    }
    const decoded = jwt(jwt_token);

    if (!decoded || !decoded.is_admin) {
      window.location.href = '/';
    }
    }, []);

  useEffect(() => {
    const syncTabWithLocation = () => setActiveTab(readTabFromLocation())
    window.addEventListener('hashchange', syncTabWithLocation)
    window.addEventListener('popstate', syncTabWithLocation)
    return () => {
      window.removeEventListener('hashchange', syncTabWithLocation)
      window.removeEventListener('popstate', syncTabWithLocation)
    }
  }, [])

  useEffect(() => {
    logSectionView('Observability', activeTab)
  }, [activeTab])

  const selectTab = (tabId) => {
    if (tabId === activeTab && window.location.hash === `#${tabId}`) return
    setActiveTab(tabId)
    window.history.pushState(
      { ...window.history.state, observabilityTab: tabId },
      '',
      `${window.location.pathname}${window.location.search}#${tabId}`
    )
  }

  const handleTabKeyDown = (event, tabIndex) => {
    let nextIndex
    if (event.key === 'ArrowRight') nextIndex = (tabIndex + 1) % OBSERVABILITY_TABS.length
    if (event.key === 'ArrowLeft') nextIndex = (tabIndex - 1 + OBSERVABILITY_TABS.length) % OBSERVABILITY_TABS.length
    if (event.key === 'Home') nextIndex = 0
    if (event.key === 'End') nextIndex = OBSERVABILITY_TABS.length - 1
    if (nextIndex === undefined) return

    event.preventDefault()
    const nextTab = OBSERVABILITY_TABS[nextIndex]
    selectTab(nextTab.id)
    document.getElementById(`observability-tab-${nextTab.id}`)?.focus()
  }

  return (
    <div className="page-content sql-page">
      <PageHeader eyebrow="Operations" title="Observability center" description="Monitor service health, agent runs, tool execution, CRM delivery, logs, and recent activity across Sayable." actions={<StatusBadge status="info">Admin only</StatusBadge>} />

      <nav className="observability-tabs" role="tablist" aria-label="Observability sections">
        {OBSERVABILITY_TABS.map((tab, index) => (
          <button
            key={tab.id}
            id={`observability-tab-${tab.id}`}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={OVERVIEW_TAB_IDS.includes(tab.id) ? 'observability-panel-overview' : `observability-panel-${tab.id}`}
            tabIndex={activeTab === tab.id ? 0 : -1}
            onClick={() => selectTab(tab.id)}
            onKeyDown={event => handleTabKeyDown(event, index)}
          >
            <span>{tab.label}</span>
            <small>{tab.description}</small>
          </button>
        ))}
      </nav>

      <section
        className="observability-tab-panel"
        id="observability-panel-overview"
        role="tabpanel"
        aria-labelledby={`observability-tab-${OVERVIEW_TAB_IDS.includes(activeTab) ? activeTab : 'health'}`}
        hidden={!OVERVIEW_TAB_IDS.includes(activeTab)}
      >
        <ObservabilityCenter view={OVERVIEW_TAB_IDS.includes(activeTab) ? activeTab : 'health'} />
      </section>
      <section className="observability-tab-panel" id="observability-panel-crm" role="tabpanel" aria-labelledby="observability-tab-crm" hidden={activeTab !== 'crm'}>
        <CrmOperations />
      </section>
      <section className="observability-tab-panel" id="observability-panel-events" role="tabpanel" aria-labelledby="observability-tab-events" hidden={activeTab !== 'events'}>
        <UserActionTable />
      </section>
    </div>
  )
}

export default AdminMetrics
